// KeepViewCurrentDialog.js
// "Keep this view current": one automation per CARD of the view, configured
// here and nowhere else. WHEN they run is the view's — every one is a daily
// 06:00 schedule, stated here and not editable — and WHAT each runs with is
// the card's: a tab per card — two tabs when one function is on the view
// twice — each running the parameters half of the same AutomationConfigForm
// the Automations tab uses, seeded from the card's current rail values (or
// the automation it already has), with dynamic dates available. Save writes
// only edited cards in ONE transaction: all or none, and a refusal names the
// card.
//
// The drafts describe the View as it was when the dialog opened. If that
// changes underneath it — a card leaves or arrives, a card's function is
// republished, or the View document is saved elsewhere — nothing here can be
// saved any more, so the dialog closes itself and its owner says "This view
// changed." (`onViewChanged`). A save already in flight finishes first.
//
// Two switched-on cards of one function with identical filters are refused —
// they would run the function twice for the same figures.
//
// A card's automation is tagged with the card, and every run it starts is
// stamped with the same identity, so the card adopts its results and shows
// their parameters on its rail (utils/viewCards.js). Email is never offered:
// a View's automation never sends mail.

import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AutomationConfigForm from '../automations/AutomationConfigForm';
import { prepareRunParameters, formatParameterErrors } from '../../utils/functionParameterUtils';
import { INCOMPLETE_DATES_MESSAGE } from '../common/DateDraftScope';
import { formatText } from '../common/TextFormatterProvider';
import { saveViewAutomations, viewTriggerId, automationConfigSnapshot } from '../../services/functionTriggers';
import {
  VIEW_SCHEDULE,
  findDuplicateCards,
  plansFromDrafts,
  cardDraftChanged,
  seedCardDraft,
  triggerForCard,
} from '../../utils/viewAutomations';

const twoDigits = (value) => String(value).padStart(2, '0');
const VIEW_TIME = `${twoDigits(VIEW_SCHEDULE.hour)}:${twoDigits(VIEW_SCHEDULE.minute)} ${VIEW_SCHEDULE.timezone}`;

function KeepViewCurrentDialog({
  open,
  onClose,
  companyName,
  viewId,
  viewName,
  // [{ instanceId, label, func: { id, name, publishedVersion, data_source_blocks }, railParameters }]
  cards,
  ownTriggers,
  tables = [],
  actorUid,
  // The View document was saved elsewhere while this dialog was open.
  stale = false,
  // Called instead of onClose when the View changed underneath the drafts.
  onViewChanged,
}) {
  const [drafts, setDrafts] = useState({});
  const [tab, setTab] = useState(0);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [allOffRequested, setAllOffRequested] = useState(false);
  // One DateDraftScope handle per mounted card form (only the open tab's).
  const dateScopeRefs = useRef({});
  // Seeded once per opening: a run landing meanwhile reshapes `cards` (its
  // rail follows the run) and must not wipe the person's edits.
  const seededRef = useRef(false);
  const baselinesRef = useRef({});
  const expectedConfigsRef = useRef({});
  // The cards the drafts were seeded for, in order, each with the published
  // version its draft describes.
  const cardsSignature = (cards || [])
    .map((card) => `${card.instanceId}@${card.func.publishedVersion}`)
    .join('|');
  const seededCardsSignatureRef = useRef('');

  useEffect(() => {
    if (!open) {
      seededRef.current = false;
      return;
    }
    if (seededRef.current) return;
    seededRef.current = true;
    const seeded = {};
    const expected = {};
    (cards || []).forEach((card) => {
      const existing = triggerForCard(viewId, card.instanceId, ownTriggers);
      seeded[card.instanceId] = seedCardDraft(card, existing, viewName);
      expected[viewTriggerId(viewId, card.instanceId)] = automationConfigSnapshot(existing);
    });
    baselinesRef.current = seeded;
    expectedConfigsRef.current = expected;
    seededCardsSignatureRef.current = cardsSignature;
    setDrafts(seeded);
    setAllOffRequested(false);
    setTab(0);
    setError('');
  }, [open, cards, cardsSignature, ownTriggers, viewId, viewName]);

  // Declared after the seeding effect so that, on opening, the signature it
  // compares against has just been recorded. It waits out a save in flight:
  // a save that succeeds closes the dialog normally, and one that fails then
  // closes it here, because the change is what made it fail.
  useEffect(() => {
    if (!open || busy) return;
    const viewChangedSinceOpening = stale || cardsSignature !== seededCardsSignatureRef.current;
    if (viewChangedSinceOpening) onViewChanged();
  }, [open, busy, stale, cardsSignature, onViewChanged]);

  const current = (cards || [])[tab] || null;
  const draft = current ? drafts[current.instanceId] : null;
  const dirtyIds = new Set((cards || []).filter((card) => (
    allOffRequested || cardDraftChanged(drafts[card.instanceId], baselinesRef.current[card.instanceId])
  )).map((card) => card.instanceId));
  const patchDraft = (instanceId, patch) => setDrafts((prev) => ({
    ...prev,
    [instanceId]: { ...prev[instanceId], ...patch },
  }));

  const handleSwitchAllOff = () => {
    // This action explicitly targets every card, including one another user
    // enabled while the dialog was open. Its opening snapshot must still match.
    setAllOffRequested(true);
    setDrafts((prev) => {
      const next = {};
      Object.entries(prev).forEach(([instanceId, value]) => { next[instanceId] = { ...value, enabled: false }; });
      return next;
    });
  };

  const handleSave = async () => {
    setError('');
    const activeScope = current ? dateScopeRefs.current[current.instanceId] : null;
    if (draft?.enabled && dirtyIds.has(current?.instanceId) && activeScope?.reveal?.()) {
      setError(INCOMPLETE_DATES_MESSAGE);
      return;
    }
    const preparedDrafts = { ...drafts };
    for (let index = 0; index < cards.length; index += 1) {
      const card = cards[index];
      const cardDraft = drafts[card.instanceId];
      if (!dirtyIds.has(card.instanceId) || !cardDraft?.enabled) continue;
      const prepared = prepareRunParameters(cardDraft.parameters, card.func.data_source_blocks, { allowDynamicDates: true });
      if (prepared.errors.length > 0) {
        setTab(index);
        setError(`${card.label}: ${formatParameterErrors(prepared.errors, card.func.data_source_blocks)}`);
        return;
      }
      preparedDrafts[card.instanceId] = { ...cardDraft, parameters: prepared.parameters };
    }
    const effectiveDrafts = { ...preparedDrafts };
    cards.forEach((card) => {
      if (dirtyIds.has(card.instanceId)) return;
      const currentTrigger = triggerForCard(viewId, card.instanceId, ownTriggers);
      effectiveDrafts[card.instanceId] = seedCardDraft(card, currentTrigger, viewName);
    });
    const duplicate = findDuplicateCards(cards, effectiveDrafts)[0];
    if (duplicate) {
      setError(`${duplicate[0].label} and ${duplicate[1].label} would run ${formatText(duplicate[0].func.name)} with identical filters. Change one, or switch one off.`);
      return;
    }

    const plans = plansFromDrafts(cards, preparedDrafts, baselinesRef.current, allOffRequested);
    if (plans.length === 0) {
      onClose();
      return;
    }

    setBusy(true);
    try {
      await saveViewAutomations(companyName, {
        viewId,
        plans,
        expectedConfigs: expectedConfigsRef.current,
        actorUid,
      });
      onClose();
    } catch (err) {
      console.error('Failed to save this view\'s automations:', err);
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const enabledCount = Object.values(drafts).filter((value) => value?.enabled).length;
  const liveEnabled = (cards || []).some((card) => triggerForCard(viewId, card.instanceId, ownTriggers)?.enabled);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Keep this view current</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {`Each card you switch on refreshes daily at ${VIEW_TIME}, run with the filters below. Automatic refreshes never send email.`}
        </Typography>

        <Tabs
          // No tab is selected in the moment between the selected card leaving
          // the View and this dialog closing itself.
          value={current ? tab : false}
          onChange={(e, next) => {
            if (current && dateScopeRefs.current[current.instanceId]?.reveal?.()) {
              setError(INCOMPLETE_DATES_MESSAGE);
              return;
            }
            setTab(next);
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {(cards || []).map((card) => (
            <Tab
              key={card.instanceId}
              label={`${formatText(card.label)}${drafts[card.instanceId]?.enabled ? ' · on' : ''}`}
              disabled={busy}
            />
          ))}
        </Tabs>

        {current && draft && (
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              sx={{ ml: 0 }}
              control={(
                <Switch
                  checked={draft.enabled}
                  disabled={busy}
                  onChange={(e) => patchDraft(current.instanceId, { enabled: e.target.checked })}
                  inputProps={{ 'aria-label': `Keep ${current.label} current` }}
                />
              )}
              label={<Typography variant="body2">Keep this card current</Typography>}
            />

            {draft.disabledReason && (
              <Alert severity="info" sx={{ mt: 1 }}>{draft.disabledReason}</Alert>
            )}
            {draft.stale && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {`This automation was configured against an older version of ${formatText(current.func.name)}. Saving runs it against version ${current.func.publishedVersion}; review its filters first.`}
              </Alert>
            )}
            <TextField
              fullWidth
              size="small"
              label="Name"
              value={draft.name}
              disabled={!draft.enabled || busy}
              onChange={(e) => patchDraft(current.instanceId, { name: e.target.value })}
              inputProps={{ maxLength: 100 }}
              sx={{ mt: 2 }}
            />

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              {`Runs daily at ${VIEW_TIME} — the view's refresh time.`}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <AutomationConfigForm
              key={current.instanceId}
              triggerType="on_schedule"
              config={draft}
              onChange={(patch) => patchDraft(current.instanceId, patch)}
              dataSourceBlocks={current.func.data_source_blocks}
              tables={tables}
              publishedVersion={current.func.publishedVersion}
              editable={draft.enabled && !busy}
              dateScopeRef={(handle) => { dateScopeRefs.current[current.instanceId] = handle; }}
              showWhen={false}
            />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Stack direction="row" spacing={1} sx={{ flex: 1 }}>
          <Button color="inherit" onClick={handleSwitchAllOff} disabled={busy || (enabledCount === 0 && !liveEnabled)}>
            Switch all off
          </Button>
        </Stack>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={busy || !current || dirtyIds.size === 0}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default KeepViewCurrentDialog;
